import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CompanyAudience } from "../../store/apis/companyApi";
import { LanguageSelect } from "../common/LanguageSelect";
import NumberField from "../common/NumberField";

type BriefingSex = "MAN" | "WOMAN" | "BOTH";

export type AudienceFormState = {
  name: string;
  description: string;
  sex: BriefingSex;
  minimumAge: string;
  maximumAge: string;
  languages: string[];
  geography: string;
  technology: string;
  interests: string;
  dataAudience: string;
  ctr: string;
  exclusions: string;
  notes: string;
  imageUrl: string;
};

type TargetFormErrors = Partial<Record<keyof AudienceFormState, string>>;

const defaultFormState: AudienceFormState = {
  name: "",
  description: "",
  sex: "BOTH",
  minimumAge: "",
  maximumAge: "",
  languages: [],
  geography: "",
  technology: "",
  interests: "",
  dataAudience: "",
  ctr: "",
  exclusions: "",
  notes: "",
  imageUrl: "",
};

interface AudienceDialogProps {
  open: boolean;
  mode: "create" | "edit";
  initialData?: CompanyAudience | null;
  onClose: () => void;
  onSubmit: (data: AudienceFormState) => void;
  isSubmitting?: boolean;
}

export const AudienceDialog: React.FC<AudienceDialogProps> = ({
  open,
  mode,
  initialData,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [formState, setFormState] =
    useState<AudienceFormState>(defaultFormState);
  const [errors, setErrors] = useState<TargetFormErrors>({});

  useEffect(() => {
    if (open) {
      if (mode === "edit" && initialData) {
        setFormState({
          name: initialData.name,
          description: initialData.description ?? "",
          sex: (initialData.sex as BriefingSex) ?? "BOTH",
          minimumAge: initialData.minimumAge
            ? String(initialData.minimumAge)
            : "",
          maximumAge: initialData.maximumAge
            ? String(initialData.maximumAge)
            : "",
          languages: initialData.language
            ? initialData.language.split(",").map((c) => c.trim())
            : [],
          geography: initialData.geography ?? "",
          technology: initialData.technology ?? "",
          interests: initialData.interests ?? "",
          dataAudience: initialData.dataAudience ?? "",
          ctr: initialData.ctr ? String(initialData.ctr) : "",
          exclusions: initialData.exclusions ?? "",
          notes: initialData.notes ?? "",
          imageUrl: initialData.imageUrl ?? "",
        });
      } else {
        setFormState(defaultFormState);
      }
      setErrors({});
    }
  }, [open, mode, initialData]);

  const sexOptions = useMemo<BriefingSex[]>(() => ["MAN", "WOMAN", "BOTH"], []);

  const handleClose = () => {
    if (isSubmitting) return;
    setErrors({});
    onClose();
  };

  const handleFieldChange = <K extends keyof AudienceFormState>(
    field: K,
    value: AudienceFormState[K]
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = (): boolean => {
    const nextErrors: TargetFormErrors = {};

    if (!formState.name.trim()) {
      nextErrors.name = t("validation.required", "This field is required");
    }

    if (formState.minimumAge && Number.isNaN(Number(formState.minimumAge))) {
      nextErrors.minimumAge = t(
        "validation.invalidNumber",
        "Enter a valid number"
      );
    }

    if (formState.maximumAge && Number.isNaN(Number(formState.maximumAge))) {
      nextErrors.maximumAge = t(
        "validation.invalidNumber",
        "Enter a valid number"
      );
    }

    if (formState.ctr && Number.isNaN(Number(formState.ctr))) {
      nextErrors.ctr = t("validation.invalidNumber", "Enter a valid number");
    }

    const minAge = Number(formState.minimumAge);
    const maxAge = Number(formState.maximumAge);
    if (
      formState.minimumAge &&
      formState.maximumAge &&
      !Number.isNaN(minAge) &&
      !Number.isNaN(maxAge) &&
      minAge > maxAge
    ) {
      nextErrors.minimumAge = t("validation.invalidNumber", "Min > Max");
      nextErrors.maximumAge = t("validation.invalidNumber", "Max < Min");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onSubmit(formState);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {mode === "create"
          ? t("company.audiences.newAudience", "New Audience")
          : t("company.audiences.editAudience", "Edit Audience")}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          {/* Main Info */}
          <Typography variant="subtitle1" fontWeight={600}>
            {t("company.basicInfo", "Basic Info")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("company.audiences.name", "Name")}
                value={formState.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                error={Boolean(errors.name)}
                helperText={errors.name}
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("company.audiences.description", "Description")}
                value={formState.description}
                onChange={(e) =>
                  handleFieldChange("description", e.target.value)
                }
                size="small"
              />
            </Grid>
          </Grid>

          {/* Demographic & Language Target */}
          <Typography variant="subtitle1" fontWeight={600} mt={2}>
            {t("company.audiences.title", "Audience Target")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label={t("company.audiences.sex", "Sex")}
                value={formState.sex}
                onChange={(event) =>
                  handleFieldChange("sex", event.target.value as BriefingSex)
                }
                size="small"
              >
                {sexOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`company.audiences.sexOptions.${option}`, option)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberField
                fullWidth
                label={t("company.audiences.minimumAge", "Minimum Age")}
                value={formState.minimumAge}
                onChange={(event) =>
                  handleFieldChange("minimumAge", event.target.value)
                }
                error={Boolean(errors.minimumAge)}
                helperText={errors.minimumAge}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberField
                fullWidth
                label={t("company.audiences.maximumAge", "Maximum Age")}
                value={formState.maximumAge}
                onChange={(event) =>
                  handleFieldChange("maximumAge", event.target.value)
                }
                error={Boolean(errors.maximumAge)}
                helperText={errors.maximumAge}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <LanguageSelect
                multiple
                label={t("company.audiences.language", "Language")}
                value={formState.languages}
                onChange={(value) =>
                  handleFieldChange("languages", value as string[])
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("company.audiences.geography", "Geography")}
                value={formState.geography}
                onChange={(e) => handleFieldChange("geography", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("company.audiences.technology", "Technology")}
                value={formState.technology}
                onChange={(e) =>
                  handleFieldChange("technology", e.target.value)
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("company.audiences.interests", "Interests")}
                value={formState.interests}
                onChange={(e) => handleFieldChange("interests", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("company.audiences.dataAudience", "Data Audience")}
                value={formState.dataAudience}
                onChange={(e) =>
                  handleFieldChange("dataAudience", e.target.value)
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("company.audiences.exclusions", "Exclusions")}
                value={formState.exclusions}
                onChange={(e) =>
                  handleFieldChange("exclusions", e.target.value)
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <NumberField
                fullWidth
                label={t("company.audiences.ctr", "Expected CTR (%)")}
                value={formState.ctr}
                onChange={(e) => handleFieldChange("ctr", e.target.value)}
                error={Boolean(errors.ctr)}
                helperText={errors.ctr}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label={t("company.audiences.imageUrl", "Image URL")}
                value={formState.imageUrl}
                onChange={(e) => handleFieldChange("imageUrl", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t("company.audiences.notes", "Notes")}
                value={formState.notes}
                onChange={(e) => handleFieldChange("notes", e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
        >
          {mode === "create"
            ? t("company.audiences.create", "Create")
            : t("common.save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
